import { useEffect } from 'react';

import { Flex } from '@chakra-ui/react';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { setSurveyCreationPresetLoadKey, useIsSurveyCreationAllPresetHandled } from 'reducers/surveyCreationSlice';

import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { SurveyCreationFooter } from './SurveyCreationFooter';
import { SurveySectionLoader } from './components/SurveyCreationLoader';
import { SurveyNameSection } from './components/SurveyNameSection';
import { SurveyReminderSection } from './components/SurveyReminderSection';
import { SurveyTypeSection } from './components/SurveyTypeSection';
import { useGetSurveySettings } from './dataHook';
import { usePresetInitializationInfo } from './usePresetInitializationInfo';

type SurveyLayoutProps = {
  surveyId?: string;
  showFooter?: boolean;
  isReadOnly?: boolean;
  children: React.ReactNode;
};

const SurveyLayout = ({ surveyId, showFooter = false, isReadOnly, children }: SurveyLayoutProps) => {
  const isL2NavVisible = useSelectIsL2Visible();

  return (
    <PaperWithShadow
      display="flex"
      flexDir="column"
      py={0}
      flex={1}
      overflow="auto"
      ml={isL2NavVisible ? '0' : '34px'}
      transition="margin .3s ease"
    >
      <Flex flex={1} p={4} pb={3} overflow="auto" borderRadius="8px" bg="white" flexDir="column">
        <Flex flex={1} p={2} borderRadius="8px" bg="surface.F5F7F9" flexDir="column" gap={2}>
          {children}
        </Flex>
      </Flex>
      {showFooter && <SurveyCreationFooter surveyId={surveyId} isReadOnly={isReadOnly} />}
    </PaperWithShadow>
  );
};

type SurveyCreationContentProps = {
  surveyId: string;
  surveyName: string;
  isReadOnly: boolean;
  selectedFilters: {
    LOCATION: (string | null)[] | null;
    COMPANY_ID: { companyName: string; id: number } | null;
  };
  totalEmployeeCount: number | null;
  revieweeSelectionType: string | null;
};

export const SurveyCreationContent = ({
  surveyId,
  surveyName,
  isReadOnly,
  selectedFilters,
  totalEmployeeCount,
  revieweeSelectionType,
}: SurveyCreationContentProps) => {
  const {
    data: surveySettings,
    isLoading: isSurveySettingsLoading,
    isError: isSurveySettingsError,
  } = useGetSurveySettings(surveyId);

  const isSurveyCreationAllPresetHandled = useIsSurveyCreationAllPresetHandled();
  const { isPresetInitialization } = usePresetInitializationInfo();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // temporary FE handle survey preset code
  // to clear 'preset' query param from current route after preset data handling
  useEffect(() => {
    if (isPresetInitialization && isSurveyCreationAllPresetHandled) {
      const queryParams = new URLSearchParams(location.search);
      queryParams.delete('preset');
      history.replace({ search: queryParams.toString() });
    }
  }, [history, isPresetInitialization, isSurveyCreationAllPresetHandled, location.search]);

  useEffect(
    () => () => {
      // on unmount, make all the isHandled states as false
      // to avoid unexpected behavior when user goes back to the survey creation page
      dispatch(
        setSurveyCreationPresetLoadKey({
          isHandledPresetNameLoad: false,
          isHandledFrequencyLoad: false,
          isHandledPresetTemplateLoad: false,
        })
      );
    },
    [dispatch]
  );

  if (isSurveySettingsLoading) {
    return (
      <SurveyLayout>
        <SurveySectionLoader />
        <SurveySectionLoader />
        <SurveySectionLoader />
      </SurveyLayout>
    );
  }

  if (isSurveySettingsError) {
    return (
      <SurveyLayout>
        <TextBodyMedium>There was an error loading the survey details. Please try again.</TextBodyMedium>
      </SurveyLayout>
    );
  }

  return (
    <SurveyLayout surveyId={surveyId} showFooter isReadOnly={isReadOnly}>
      <SurveyNameSection
        surveyId={surveyId}
        defaultSurveyName={surveyName}
        surveyStartDate={surveySettings.startDate}
        surveyEndDate={surveySettings.endDate}
        isReadOnly={isReadOnly}
        selectedFilters={selectedFilters}
        totalEmployeeCount={totalEmployeeCount}
        revieweeSelectionType={revieweeSelectionType}
      />
      <SurveyTypeSection
        surveyId={surveyId}
        defaultEmployeePrivacy={surveySettings.visibilityType}
        isReadOnly={isReadOnly}
      />
      <SurveyReminderSection surveyId={surveyId} isReadOnly={isReadOnly} />
    </SurveyLayout>
  );
};
